import React, { useState, useEffect } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import { getApiBaseUrl } from "../../utils/utils";

export default function NormalTaskCreation() {
  const API_URL = process.env.REACT_APP_API_URL;

  // Form State
  const [scaninfo, setScaninfo] = useState({
    task_file: null,
    task_creation_date: "",
    partner_id: "",
  });

  // Data State
  const [partners, setPartners] = useState([]);
  const [partnerInfo, setPartnerInfo] = useState(null);

  // UI State
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Helper functions
  const getUserId = () => localStorage.getItem("userid") || "";
  const getUserName = () => localStorage.getItem("username") || "";
  const getUserEmail = () => localStorage.getItem("useremail") || "";

  // Clean string helper
  const cleanString = (value) => {
    if (value === null || value === undefined) return "";
    return String(value).trim();
  };

  // Clean phone number
  const cleanPhone = (value) => {
    if (!value) return "";
    let phone = String(value).replace(/[^\d+]/g, "");
    if (phone.startsWith("971")) phone = "+" + phone;
    else if (phone.startsWith("0")) phone = "+971" + phone.substring(1);
    else if (!phone.startsWith("+")) phone = "+971" + phone;
    return phone;
  };

  // Clean number
  const cleanNumber = (value, defaultVal = 0) => {
    if (value === null || value === undefined || value === "") return defaultVal;
    const num = parseFloat(String(value).replace(/[^\d.-]/g, ""));
    return isNaN(num) ? defaultVal : num;
  };

  // Clean integer
  const cleanInteger = (value, defaultVal = 0) => {
    if (value === null || value === undefined || value === "") return defaultVal;
    const num = parseInt(String(value).replace(/[^\d-]/g, ""), 10);
    return isNaN(num) ? defaultVal : num;
  };

  // Format date for API
  const formatDateForAPI = (dateStr) => {
    if (!dateStr) return "";
    const str = String(dateStr).trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return str;
    const parts = str.split(/[\/\-]/);
    if (parts.length === 3) {
      if (parts[0].length === 4) return str;
      return `${parts[2]}-${parts[1].padStart(2, "0")}-${parts[0].padStart(2, "0")}`;
    }
    return "";
  };

  // Fetch partners on mount using CRM API
  useEffect(() => {
    const fetchPartners = async () => {
      try {
        const baseApiUrl = getApiBaseUrl();
        const response = await axios.get(`${baseApiUrl}/v1.0/partner/retrieve_list?page=0`);
        setPartners(response.data.data || []);
      } catch (err) {
        console.error("Failed to fetch partners:", err);
      }
    };
    fetchPartners();
  }, []);

  // Fetch partner info when partner selected using CRM API
  useEffect(() => {
    if (!scaninfo.partner_id) {
      setPartnerInfo(null);
      return;
    }
    const fetchPartnerInfo = async () => {
      try {
        const baseApiUrl = getApiBaseUrl();
        const response = await fetch(`${baseApiUrl}/v1.0/partner/retrieve?partnerId=${scaninfo.partner_id}`);
        if (!response.ok) throw new Error("Failed to fetch partner details");
        const { data } = await response.json();
        const { pname, email, ph, addr, lat, lon } = data;
        const addressParts = addr?.split(" - ") || [];
        const pkpCity = addressParts.length > 1 ? addressParts[addressParts.length - 1] : "Dubai";
        setPartnerInfo({
          pkpName: pname,
          pkpEmail: email,
          pkpMobno: ph,
          pkpAdd: addr,
          pkpCity: pkpCity,
          pkpLat: lat,
          pkpLon: lon,
        });
      } catch (err) {
        console.error("Failed to fetch partner info:", err);
      }
    };
    fetchPartnerInfo();
  }, [scaninfo.partner_id]);

  // Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!scaninfo.task_file || !scaninfo.partner_id || !scaninfo.task_creation_date) {
      setError("Please fill all required fields");
      return;
    }

    setLoading(true);
    setError("");
    setSuccess("");

    try {
      // Parse Excel file
      const fileData = await scaninfo.task_file.arrayBuffer();
      const workbook = XLSX.read(fileData, { type: "array" });
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      const jsonData = XLSX.utils.sheet_to_json(worksheet);

      if (jsonData.length === 0) {
        setError("No data found in file");
        setLoading(false);
        return;
      }

      // Build genOrders array
      const genOrders = jsonData.map((row) => {
        const address = cleanString(row["Delivery Adress"]);
        const aptDetails = cleanString(row["Apt Details"]);
        const fullAddress = aptDetails ? `${address}, ${aptDetails}` : address;

        return {
          OpsReference: cleanString(row["Ops Reference"]),
          recvrName: cleanString(row["Receiver's Name"]),
          rcvrMobno: cleanPhone(row["Receiver's Mobile Number"]),
          delvAdd: fullAddress,
          Dcity: cleanString(row["City"]),
          pkpName: cleanString(partnerInfo?.pkpName),
          pkpMobno: cleanPhone(partnerInfo?.pkpMobno),
          pkpAdd: cleanString(partnerInfo?.pkpAdd),
          pkpCity: cleanString(partnerInfo?.pkpCity),
          pkpLat: cleanString(partnerInfo?.pkpLat),
          pkpLon: cleanString(partnerInfo?.pkpLon),
          pkpEmail: cleanString(partnerInfo?.pkpEmail),
          itemWgt: cleanNumber(row["KG Weight"], 0.1),
          noOfitems: cleanInteger(row["noOfitems"], 1),
          itemLen: cleanNumber(row["Length"], 0),
          itemWid: cleanNumber(row["Width"], 0),
          itemHt: cleanNumber(row["Height"], 0),
          codAmt: cleanNumber(row["Amount to be Recieved (AED)"], 0),
          itemDesc: cleanString(row["Item Description"]),
          slot: cleanInteger(row["Slot"], 1),
          booking_date: formatDateForAPI(row["booking_date"]) || scaninfo.task_creation_date,
        };
      });

      // Build payload
      const payload = {
        userId: parseInt(getUserId()),
        orders: [
          {
            partnerId: parseInt(scaninfo.partner_id),
            genOrders: genOrders
          }
        ]
      };

      console.log("Submitting payload:", JSON.stringify(payload, null, 2));

      const response = await axios.post(`${API_URL}shypvdriverapp/api/batch-tasks/create`, payload);

      if (response.data) {
        setSuccess(`Successfully submitted ${genOrders.length} orders!`);
        // Reset form
        setScaninfo({
          task_file: null,
          task_creation_date: "",
          partner_id: "",
        });
        // Reset file input
        const fileInput = document.getElementById("taskFile");
        if (fileInput) fileInput.value = "";
      }
    } catch (err) {
      console.error("Submit error:", err);
      setError(err.response?.data?.message || err.message || "Failed to submit orders");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container-fluid" style={{ padding: "20px" }}>
      <div className="row">
        <div className="col-md-12">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
            <h4 style={{ margin: 0 }}>Normal Task Creation</h4>
            <a
              href="/bulk_template/normal_task.csv"
              download="normal_bulk_upload.csv"
              className="btn btn-primary"
              style={{ textTransform: "uppercase", fontWeight: "bold" }}
            >
              Download Template
            </a>
          </div>

          {error && (
            <div className="alert alert-danger" role="alert">
              {error}
            </div>
          )}
          {success && (
            <div className="alert alert-success" role="alert">
              {success}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="form-group" style={{ marginBottom: "15px" }}>
              <label><strong>Date:</strong></label>
              <input
                type="date"
                className="form-control"
                value={scaninfo.task_creation_date}
                onChange={(e) => setScaninfo({ ...scaninfo, task_creation_date: e.target.value })}
                required
              />
            </div>

            <div className="form-group" style={{ marginBottom: "15px" }}>
              <label><strong>Partner</strong></label>
              <select
                className="form-control"
                value={scaninfo.partner_id}
                onChange={(e) => setScaninfo({ ...scaninfo, partner_id: e.target.value })}
                required
              >
                <option value="">select</option>
                {partners.map((p) => (
                  <option key={p.pid} value={p.pid}>{p.pname}</option>
                ))}
              </select>
            </div>

            <div className="form-group" style={{ marginBottom: "20px" }}>
              <label><strong>Task File:</strong></label>
              <input
                type="file"
                id="taskFile"
                className="form-control"
                accept=".xlsx,.xls,.csv"
                onChange={(e) => setScaninfo({ ...scaninfo, task_file: e.target.files[0] })}
                required
              />
            </div>

            <div style={{ textAlign: "center" }}>
              <button
                type="submit"
                className="btn btn-info"
                disabled={loading}
                style={{ padding: "10px 40px" }}
              >
                {loading ? "Submitting..." : "Submit"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
